import { type ReactNode } from "react";

/**
 * A tiny, dependency-free JSON syntax highlighter. Takes an
 * already-formatted JSON string (or any text) and renders it inside a
 * scrollable `<pre>`, colouring keys, string values, numbers, and the
 * `true`/`false`/`null` literals via the design-token text utilities so
 * it tracks the active theme. Non-JSON text simply renders with few
 * (harmless) highlights.
 *
 * We tokenize rather than pull in a highlighter dependency: the support
 * console only ever shows AI wire envelopes, so a single regex over the
 * pretty-printed string is enough and keeps the bundle lean.
 */

export type JsonHighlightProps = {
  /** Pre-formatted text to display (typically `JSON.stringify(v, null, 2)`). */
  readonly text: string;
  /** Extra classes for the scroll container. */
  readonly className?: string;
};

// One pass, non-overlapping. Order matters: a quoted string is matched
// (and consumed) before its inner characters can be seen as numbers, and
// a string immediately followed by `:` is a key.
const TOKEN =
  /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

function tokenize(src: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of src.matchAll(TOKEN)) {
    const start = m.index ?? 0;
    if (start > last) out.push(src.slice(last, start));
    const [full, str, colon, keyword, num] = m;
    if (str !== undefined) {
      if (colon !== undefined) {
        // `"name":` — a property key plus its colon.
        out.push(
          <span key={key++} className="text-accent">
            {str}
          </span>,
        );
        out.push(
          <span key={key++} className="text-dim">
            {colon}
          </span>,
        );
      } else {
        out.push(
          <span key={key++} className="text-success">
            {str}
          </span>,
        );
      }
    } else if (keyword !== undefined) {
      out.push(
        <span key={key++} className="text-info">
          {keyword}
        </span>,
      );
    } else if (num !== undefined) {
      out.push(
        <span key={key++} className="text-warning">
          {num}
        </span>,
      );
    }
    last = start + full.length;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}

export function JsonHighlight({ text, className }: JsonHighlightProps) {
  return (
    <pre
      className={`m-0 overflow-auto whitespace-pre font-mono text-[13px] leading-relaxed text-fg ${
        className ?? ""
      }`}
    >
      <code>{tokenize(text)}</code>
    </pre>
  );
}
